<template>
    <div
        class="rounded"
        :class="[
            requiresMargin ? 'mb-4' : '',
            inputDisabled ? 'cursor-not-allowed opacity-75' : '',
            !noPadding ? 'border-primary-400/20 bg-primary-500/30 border p-3' : 'inline-flex items-center',
        ]"
        @click="toggle"
    >
        <div v-if="!noPadding" class="flex justify-between gap-4" :class="hasFooter ? 'items-start' : 'items-center'">
            <div class="min-w-0 flex-1">
                <label
                    class="text-primary-200 block text-sm uppercase leading-tight"
                    :class="[small ? '' : 'tracking-wide']"
                    :for="id"
                    v-if="labelStr"
                    @click="toggle"
                >
                    <t :path="labelStr" />
                </label>

                <p class="small text-primary-300 mt-1 max-w-prose leading-relaxed" v-if="footer">
                    <t :path="footer" />
                </p>
            </div>

            <div class="switch shrink-0" :class="{ active: input, small: small }">
                <input v-model="input" :id="id" type="checkbox" :name="name" :disabled="inputDisabled" :readonly="readonly" />
                <v-button type="button" :class="{ [inactiveBg]: !input }" />
            </div>
        </div>

        <div v-else class="inline-flex items-center">
            <div class="switch" :class="{ active: input, small: small }">
                <input v-model="input" :id="id" type="checkbox" :name="name" :disabled="inputDisabled" :readonly="readonly" />
                <v-button type="button" :class="{ [inactiveBg]: !input }" />
            </div>

            <div class="ml-4 min-w-0" v-if="!hideLabel">
                <label class="text-primary-200 block text-sm uppercase tracking-wide" :for="id" v-if="labelStr" @click="toggle">
                    <t :path="labelStr" />
                </label>

                <p class="small text-primary-300 mt-1" v-if="footer">
                    <t :path="footer" />
                </p>
            </div>
        </div>
    </div>
</template>

<style lang="css" scoped>
@reference "../../assets/css/wisp.css";

.switch {
    @apply flex cursor-pointer items-center;

    input {
        @apply hidden;
    }

    button {
        @apply relative rounded-full border-none;

        height: 18px;
        min-height: 0;
        width: 36px;
        transition: 0.2s ease-in-out background-color;

        &::before {
            @apply bg-primary-200 absolute block rounded-full;

            content: '';
            top: -2px;
            left: -2px;
            width: 22px;
            height: 22px;
            transition:
                0.2s ease-in-out transform,
                0.2s ease-in-out background-color;
            transform: translateX(0);
        }
    }
}

.switch.small {
    button {
        height: 16px;
        width: 32px;

        &::before {
            /* The default size overhangs its track by 2px on every side; an
               18px knob on this 16/32 track only overhangs by 1px, so keeping
               the inherited -2px offsets parked it high and left of center. */
            top: -1px;
            left: -1px;
            width: 18px;
            height: 18px;
        }
    }
}

.switch.active {
    @apply shadow-none;

    button {
        /* Accent-tinted so the on-state fills the track. primary-500/30 here
           composited to within 0.01 of the row behind it, so "on" and "off"
           tracks were the same colour and only the knob moved. */
        @apply bg-accent-500/30;

        &::before {
            @apply bg-accent-500;
            /* travel = width - knob + 2·overhang; the small variant (32/18) lands on the same 16px */
            transform: translateX(16px);
        }
    }
}
</style>

<script lang="ts">
import { computed, defineComponent, inject, onBeforeMount, onBeforeUnmount, ref, useId, watch } from 'vue';
import { useI18n } from 'vue-i18n';

export default defineComponent({
    name: 'FormSwitch',

    emits: ['update:value'],
    props: {
        name: {
            type: String,
            required: true,
        },
        value: {
            type: Boolean,
        },
        label: {
            type: [String, Array],
        },
        hideLabel: {
            type: Boolean,
        },
        noMargin: {
            type: Boolean,
        },
        noPadding: {
            type: Boolean,
            default: false,
        },
        footer: {
            type: String,
        },
        disabled: {
            type: Boolean,
        },
        readonly: {
            type: Boolean,
        },
        small: {
            type: Boolean,
            default: false,
        },
        inactiveBg: {
            type: String,
            /* A recessed groove. This was primary-600, which is *lighter* than
               the primary-500/30 row a switch normally sits in — so the track
               vanished and the switch read as a knob floating on nothing. */
            default: 'bg-primary-900/60',
        },
    },

    setup(props, { emit }) {
        const { t } = useI18n();
        const input = ref(props.value || false);
        watch(
            () => props.value,
            (newValue) => {
                input.value = newValue;
            },
        );

        watch(
            () => input.value,
            (newValue) => {
                emit('update:value', newValue);
            },
        );

        let unregister: any;

        onBeforeMount(() => {
            unregister = inject<registerFormComponentFn | null>(
                'registerFormComponent',
                null,
            )?.({
                key: props.name,
                value: input,
                onSubmit: (state) => {
                    if (props.disabled) return;

                    inputDisabled.value = state;
                },
            });
        });

        onBeforeUnmount(() => {
            unregister?.();
            unregister = null;
        });

        const inputDisabled = ref(!!props.disabled); // Copy disabled as otherwise disabled fields will behave weirdly due to ref modifying props.disabled directly.

        // Watch for changes to disabled prop
        watch(
            () => props.disabled,
            (newValue) => {
                inputDisabled.value = !!newValue;
            },
        );

        const labelStr = computed(() => {
            if (props.hideLabel) return null;
            if (props.label) return props.label;

            return `components.form.fields.${props.name}`;
        });
        const hasFooter = computed(() => Boolean(props.footer?.trim()));

        const id = useId();

        return {
            t,
            input,
            inputDisabled,
            labelStr,
            hasFooter,
            id: computed(() => `${id}-${props.name}-switch`),
            requiresMargin: computed(() => {
                if (props.hideLabel || props.noMargin) {
                    return false;
                }

                return true;
            }),

            toggle: () => {
                if (inputDisabled.value) return;

                input.value = !input.value;
            },
        };
    },
});
</script>
